<template>
  <login-wrap
    id="login-setup-2fa"
    :title="string.title">
    <label id="login-setup-subtitle">
      {{ string.subTitle }}
    </label>
    <label class="login-setup-step"> 1. <span v-html="$sanitize(string.authAppsText)" /> </label>
    <p
      v-if="code2fa"
      id="qr-code-2fa">
      {{ code2fa }}
      <copy-button
        id="qr-code-2fa-copy"
        :title="string.copyLabel"
        :string-to-copy="code2fa" />
    </p>
    <div
      v-if="imgSrc"
      id="qr-code">
      <img
        id="qr-code-img"
        :src="`data:image/png;base64,${imgSrc}`"
        :alt="string.qrCodeAlt">
    </div>
    <label class="login-setup-step"> 2. <span v-html="$sanitize(string.authCodeText)" /> </label>
    <valid-form
      class="verification-code-form"
      @submit="verifyAction">
      <verification-code-input
        v-model="code"
        :aria-label="string.qrCodeAlt"
        :is-error="isCodeError"
        class="verification-code-input" />
      <qx-button
        id="verify"
        :title="string.verifyButtonLabel"
        :is-block="true"
        :in-progress="inProgress"
        type="submit"
        state="active" />
    </valid-form>
    <another-account />
  </login-wrap>
</template>

<script>
import { mapMutations } from 'vuex';
import { Form as ValidForm } from 'vee-validate';

import LoginWrap from '@/components/auth/LoginWrap.vue';
import anotherAccount from '@/elements/auth/anotherAccount.vue';
import qxButton from '@/elements/button.vue';
import VerificationCodeInput from '@/elements/VerificationCodeInput.vue';
import EventBus from '@/elements/eventBus.js';
import CopyButton from '@/elements/activity/CopyButton.vue';

import { logErrorWithSentry } from '@/elements/utils.js';

import { mfaLogin, get2faCodes, setMfa } from '@/api';
import { INVALID_2FA_CODE } from '@/utils/cons';

export default {
  name: 'LoginSetup2fa',
  components: {
    LoginWrap,
    anotherAccount,
    qxButton,
    VerificationCodeInput,
    ValidForm,
    CopyButton,
  },
  emits: ['successLogin'],
  data() {
    return {
      inProgress: false,
      imgSrc: '',
      code2fa: '',
      code: '',
      isCodeError: false,
    };
  },
  computed: {
    string() {
      return {
        title: this.$gettext('Two-factor authentication'),
        qrCodeAlt: this.$gettext('QR Code'),
        verifyButtonLabel: this.$gettext('Verify'),
        copyLabel: this.$gettext('Copy to clipboard'),
        subTitle: this.$gettext('Two-factor authentication (2FA) is required for your Quatrix account.'),
        authAppsText: this.$gettextInterpolate(
          this.$gettext(
            'Scan the QR code using any authentication application on your phone(e.g. %{ googleTag }, %{ duoTag }, %{ authyTag }) or enter the following code:'
          ),
          {
            googleTag:
              '<a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" rel="noopener noreferrer" target="_blank">Google Authenticator</a>',
            duoTag:
              '<a href="http://guide.duosecurity.com/third-party-accounts" rel="noopener noreferrer" target="_blank">Duo Mobile</a>',
            authyTag: '<a href="https://www.authy.com/" rel="noopener noreferrer" target="_blank">Authy</a>',
          },
          true
        ),
        authCodeText: this.$gettextInterpolate(
          this.$gettext(
            'Enter the 6 figure confirmation code shown on the app. %{ learnTag } Learn more. %{ closeTag }'
          ),
          {
            learnTag:
              '<a href="https://docs.maytech.net/quatrix/quatrix-user-guide/security-and-privacy-options/secure-passwords-and-2fa#SecurePasswordsand2FA-Enabling2FA" rel="noopener noreferrer" target="_blank">',
            closeTag: '</a>',
          },
          true
        ),
      };
    },
  },
  mounted() {
    this.generateQRCode();
  },
  methods: {
    ...mapMutations({
      setLoginError: 'auth/setLoginError',
    }),
    async generateQRCode() {
      try {
        const { data } = await get2faCodes();
        this.imgSrc = data.image;
        this.code2fa = data.code;
      } catch (error) {
        EventBus.$emit('handle-error', { response: error?.response });
        logErrorWithSentry(error);
      }
    },
    async verifyAction() {
      try {
        if (this.inProgress) {
          return;
        }
        this.inProgress = true;
        this.isCodeError = false;
        await setMfa('profile', {
          auth_type: '2FA',
          code: this.code,
        });
        this.$notify({
          text: this.$gettext('Two factor authentication is activated for you'),
          type: 'success',
        });
        this.loginAction();
      } catch (error) {
        if (error?.response?.status === 400 && error?.response?.data?.code === 10) {
          this.isCodeError = true;
          EventBus.$emit('handle-error', {
            response: error.response,
            options: {
              errorMsg: this.$gettext('Two factor authentication failed'),
            },
          });
        } else {
          EventBus.$emit('handle-error', { response: error?.response });
          logErrorWithSentry(error);
        }
      } finally {
        this.inProgress = false;
      }
    },
    async loginAction() {
      try {
        const { status } = await mfaLogin({
          auth_type: '2FA',
          code: this.code,
        });
        if (status === 200) {
          this.$emit('successLogin');
        }
      } catch (error) {
        if (error?.response?.status === 401 && error?.response?.data?.code === INVALID_2FA_CODE) {
          this.setLoginError('wrong2faCode');
        } else {
          EventBus.$emit('handle-error', { response: error?.response });
          logErrorWithSentry(error);
        }
      } finally {
        this.inProgress = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#login-setup-2fa {
  max-width: 500px;
  -webkit-font-smoothing: antialiased !important;
  #login-setup-subtitle {
    color: $gray-900;
    font-weight: 600;
    text-align: center;
  }
  .login-setup-step {
    text-align: center;
    color: $gray-900;
    font-weight: 700;
    margin-bottom: 21px;
    max-width: 500px;
    span {
      margin: 0;
      font-weight: 400;
    }
  }
  #qr-code {
    width: 150px;
    height: 150px;
    margin: 0 auto 15px;
  }
  #qr-code-2fa {
    margin: 0;
    text-align: center;
    position: relative;
    line-height: 26px;
    font-weight: 600;
  }
  #qr-code-2fa-copy {
    position: absolute;
    top: 2px;
    padding-left: 10px;
    color: $base-blue;
    font-size: 20px;
  }
  #qr-code-img {
    width: 150px;
    height: 150px;
  }
  #code-2fa {
    width: 100%;
    margin: 6px 0;
  }
  #verify {
    width: 100%;
    display: inline-block;
    color: white;
  }
  .verification-code-input {
    margin-bottom: 36px;
  }
}
</style>
